import { onAuthStateChanged, User } from 'firebase/auth';
import { auth } from '../sp-firebase/firebase';

// Cast import.meta to any to avoid TS complaints in environments without Vite types
const API_URL = (import.meta as any).env?.VITE_API_URL || 'http://localhost:8080';

export interface FxLatestResponse {
  success: boolean;
  base: string;
  rates: Record<string, number>;
  asOf: string | null;
}

async function waitForFirebaseUser(timeoutMs: number = 5000): Promise<User | null> {
  if (auth.currentUser) {
    return auth.currentUser;
  }

  return new Promise((resolve) => {
    let resolved = false;
    let unsubscribe: (() => void) | null = null;

    const timer = setTimeout(() => {
      if (resolved) return;
      resolved = true;
      if (unsubscribe) {
        unsubscribe();
      }
      resolve(null);
    }, timeoutMs);

    unsubscribe = onAuthStateChanged(auth, (user) => {
      if (resolved) return;
      resolved = true;
      clearTimeout(timer);
      if (unsubscribe) {
        unsubscribe();
      }
      resolve(user);
    });
  });
}

async function getAuthHeaders(): Promise<Record<string, string>> {
  const user = await waitForFirebaseUser();
  if (!user) {
    throw new Error('Not authenticated');
  }

  const token = await user.getIdToken();
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

export async function getLatestFxRates(base: string = 'USD'): Promise<FxLatestResponse> {
  const headers = await getAuthHeaders();

  const response = await fetch(
    `${API_URL}/api/v2/fx/latest?base=${encodeURIComponent(base.toUpperCase())}`,
    { headers },
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || 'Failed to fetch FX rates');
  }

  return (await response.json()) as FxLatestResponse;
}
